// Pure view layer for the Passing Report (?live=1, Ben 2026-10-20 — Catalyst's Passing page, made
// explorable: every bar segment and band is a set of passes you can click to highlight on an open
// Pass Map, or drag onto one). DOM-free and unit-tested; PassingReportModule paints it.
//
// Step 1 works everything out from the player's own passes, by the same rules as Catalyst's engine
// (soccer-aggregates v1.7.0 classifierHelpers / aggregateDefinitions / playerMetrics), so the numbers
// match theirs. The engine's pitch is ours (114 × 74, final third from x = 76):
// - a pass: not offside (outcome 76); successful: no outcome;
// - xPass = pass_success_probability (missing → 0 in the sums, in no band); hard < 0.5 ≤ medium ≤ 0.8 < easy;
// - Avg Difficulty = (passes − expected) / passes; PSAX = successful − expected;
// - direction by the pass's StatsBomb angle: forward |a| < 30°, backward |a| > 150°, else sideways
//   (no angle → none);
// - final third (Catalyst's "Final Third Direction Mix"): completed open-play passes that start or
//   end in the final third; open play = not a corner / free kick / throw-in / goal kick / kick-off;
// - pass length = the straight start → end distance; long ball = length ≥ 35 (Long Ball Success % =
//   completed ÷ tried); Average X = mean start x of completed passes;
// - into pressure / danger = the enriched pass_into flags; left foot % of footed passes (body part
//   38 left, 40 right).

import type { ProtoEvent } from "@catbento/shared";
import { passOf, type SbPass } from "../engines/event-map-kit.js";

const OFFSIDE_OUTCOME = 76;
const SET_PIECE_PASS_TYPES: ReadonlySet<number> = new Set([61, 62, 63, 65, 67]);
const FINAL_THIRD_X = 76;
const LONG_BALL_LENGTH = 35;
const LEFT_FOOT = 38;
const RIGHT_FOOT = 40;

/** A set of passes the report can pick out (click → highlighted on an open Pass Map; drag → filter). */
export type PassSetKey =
  | "hard"
  | "medium"
  | "easy"
  | "forward"
  | "sideways"
  | "backward"
  | "f3Forward"
  | "f3Sideways"
  | "f3Backward"
  | "f3"
  | "longBall"
  | "intoPressure"
  | "intoDanger"
  | "leftFoot";

/** The words for a set, as its chip / drag label reads ("Hard passes"). */
export const PASS_SET_LABEL: Record<PassSetKey, string> = {
  hard: "Hard passes",
  medium: "Medium passes",
  easy: "Easy passes",
  forward: "Forward passes",
  sideways: "Sideways passes",
  backward: "Backward passes",
  f3Forward: "Final third, forward",
  f3Sideways: "Final third, sideways",
  f3Backward: "Final third, backward",
  f3: "Final third passes",
  longBall: "Long balls",
  intoPressure: "Passes into pressure",
  intoDanger: "Passes into danger",
  leftFoot: "Left-foot passes",
};

interface Enriched {
  pass_into?: { pressure?: boolean; danger?: boolean };
}

function attrs(e: ProtoEvent): SbPass {
  return passOf(e) ?? {};
}
function isPass(e: ProtoEvent): boolean {
  return attrs(e).outcome?.id !== OFFSIDE_OUTCOME;
}
function xpassOf(e: ProtoEvent): number | null {
  const v = attrs(e).pass_success_probability;
  return typeof v === "number" ? v : null;
}
function bandOf(e: ProtoEvent): "hard" | "medium" | "easy" | null {
  const p = xpassOf(e);
  if (p === null) return null;
  return p < 0.5 ? "hard" : p <= 0.8 ? "medium" : "easy";
}
function directionOf(e: ProtoEvent): "forward" | "sideways" | "backward" | null {
  const a = attrs(e).angle;
  if (typeof a !== "number") return null;
  const abs = Math.abs(a);
  return abs < Math.PI / 6 ? "forward" : abs > (5 * Math.PI) / 6 ? "backward" : "sideways";
}
function isOpenPlay(e: ProtoEvent): boolean {
  const id = attrs(e).type?.id;
  return id === undefined || !SET_PIECE_PASS_TYPES.has(id);
}
function isFinalThirdCompleted(e: ProtoEvent): boolean {
  return !attrs(e).outcome && isOpenPlay(e) && (e.x >= FINAL_THIRD_X || e.x_end >= FINAL_THIRD_X);
}
function lengthOf(e: ProtoEvent): number {
  return Math.hypot(e.x_end - e.x, e.y_end - e.y);
}
function intoOf(e: ProtoEvent): Enriched["pass_into"] {
  return (e.enriched_event_attributes as Enriched | undefined)?.pass_into;
}

/** Is a pass in a set? The same rules as the numbers below. Pure. */
export function inPassSet(key: PassSetKey, e: ProtoEvent): boolean {
  if (!isPass(e)) return false;
  switch (key) {
    case "hard":
    case "medium":
    case "easy":
      return bandOf(e) === key;
    case "forward":
    case "sideways":
    case "backward":
      return directionOf(e) === key;
    case "f3Forward":
      return isFinalThirdCompleted(e) && directionOf(e) === "forward";
    case "f3Sideways":
      return isFinalThirdCompleted(e) && directionOf(e) === "sideways";
    case "f3Backward":
      return isFinalThirdCompleted(e) && directionOf(e) === "backward";
    case "f3":
      return isFinalThirdCompleted(e);
    case "longBall":
      return typeof attrs(e).length === "number" && attrs(e).length! >= LONG_BALL_LENGTH;
    case "intoPressure":
      return intoOf(e)?.pressure === true;
    case "intoDanger":
      return intoOf(e)?.danger === true;
    case "leftFoot":
      return attrs(e).body_part?.id === LEFT_FOOT;
  }
}

/** One segment of a share bar (or a band row): its set, count and share of the bar's total. */
export interface ShareSegment {
  key: PassSetKey;
  /** The Stats file column for its position average (step 2). */
  avgKey?: string;
  label: string;
  count: number;
  /** 0–1 of the bar's total (0 with none). */
  share: number;
}

export interface BandRow {
  key: "hard" | "medium" | "easy";
  /** The Stats file column for the position's success % in this band. */
  avgKey: string;
  label: string;
  attempted: number;
  completed: number;
  expected: number;
  /** null with none attempted. */
  successPct: number | null;
  expectedPct: number | null;
  psax: number;
}

export interface Tendency {
  label: string;
  value: string;
  /** The set it picks out, when it is one (Long Balls, Into Pressure…). */
  key?: PassSetKey;
  /** The Stats file column for its position average, and how to show it. */
  avgKey?: string;
  avgType?: "percentage" | "decimal";
  definition: string;
}

export interface PassingReport {
  passes: number;
  successful: number;
  expected: number;
  /** 0–1, null with no passes. */
  successPct: number | null;
  expectedPct: number | null;
  psax: number;
  avgDifficulty: number | null;
  attemptMix: ShareSegment[];
  directionMix: ShareSegment[];
  finalThirdMix: ShareSegment[];
  bands: BandRow[];
  tendencies: Tendency[];
}

function share(key: PassSetKey, label: string, count: number, total: number, avgKey?: string): ShareSegment {
  return { key, label, count, share: total ? count / total : 0, avgKey };
}

const pct1 = (v: number | null) => (v === null ? "—" : `${(v * 100).toFixed(1)}%`);

export function passingReport(events: readonly ProtoEvent[]): PassingReport {
  const passes = events.filter(isPass);
  const n = passes.length;
  let successful = 0;
  let expected = 0;
  const band = { hard: { a: 0, c: 0, x: 0 }, medium: { a: 0, c: 0, x: 0 }, easy: { a: 0, c: 0, x: 0 } };
  const dir = { forward: 0, sideways: 0, backward: 0 };
  const f3 = { forward: 0, sideways: 0, backward: 0 };
  let f3All = 0;
  let lengthTotal = 0;
  let longTried = 0;
  let longDone = 0;
  let xTotal = 0;
  let intoPressure = 0;
  let intoDanger = 0;
  let left = 0;
  let right = 0;
  for (const e of passes) {
    const p = attrs(e);
    const done = !p.outcome;
    const xp = xpassOf(e);
    if (done) {
      successful++;
      xTotal += e.x;
    }
    if (xp !== null) expected += xp;
    const b = bandOf(e);
    if (b) {
      band[b].a++;
      if (done) band[b].c++;
      band[b].x += xp!;
    }
    const d = directionOf(e);
    if (d) dir[d]++;
    if (isFinalThirdCompleted(e)) {
      f3All++;
      if (d) f3[d]++;
    }
    lengthTotal += lengthOf(e);
    if (typeof p.length === "number" && p.length >= LONG_BALL_LENGTH) {
      longTried++;
      if (done) longDone++;
    }
    const into = intoOf(e);
    if (into?.pressure === true) intoPressure++;
    if (into?.danger === true) intoDanger++;
    if (p.body_part?.id === LEFT_FOOT) left++;
    if (p.body_part?.id === RIGHT_FOOT) right++;
  }
  const banded = band.hard.a + band.medium.a + band.easy.a;
  const directed = dir.forward + dir.sideways + dir.backward;
  const div = (a: number, b: number) => (b ? a / b : null);
  const bandRow = (key: "hard" | "medium" | "easy", label: string): BandRow => {
    const r = band[key];
    return {
      key,
      avgKey: `${key}_pass_success_perc`,
      label,
      attempted: r.a,
      completed: r.c,
      expected: r.x,
      successPct: div(r.c, r.a),
      expectedPct: div(r.x, r.a),
      psax: r.c - r.x,
    };
  };
  return {
    passes: n,
    successful,
    expected,
    successPct: div(successful, n),
    expectedPct: div(expected, n),
    psax: successful - expected,
    avgDifficulty: div(n - expected, n),
    attemptMix: [
      share("hard", "Hard", band.hard.a, banded, "hard_pass_perc"),
      share("medium", "Medium", band.medium.a, banded, "medium_pass_perc"),
      share("easy", "Easy", band.easy.a, banded, "easy_pass_perc"),
    ],
    directionMix: [
      share("forward", "Forward", dir.forward, directed, "forward_pass_perc"),
      share("sideways", "Sideways", dir.sideways, directed, "sideways_pass_perc"),
      share("backward", "Backward", dir.backward, directed, "backward_pass_perc"),
    ],
    finalThirdMix: [
      share("f3Forward", "Forward", f3.forward, f3All, "op_f3_forward_pass_perc"),
      share("f3Sideways", "Sideways", f3.sideways, f3All, "op_f3_sideways_pass_perc"),
      share("f3Backward", "Backward", f3.backward, f3All, "op_f3_backward_pass_perc"),
    ],
    bands: [bandRow("hard", "Hard"), bandRow("medium", "Medium"), bandRow("easy", "Easy")],
    tendencies: [
      {
        label: "Pass Length",
        value: n ? (lengthTotal / n).toFixed(1) : "—",
        avgKey: "pass_length",
        avgType: "decimal",
        definition: "Average pass length, start to end.",
      },
      {
        label: "Long Ball Success %",
        value: pct1(div(longDone, longTried)),
        key: "longBall",
        avgKey: "long_ball_perc",
        avgType: "percentage",
        definition: `Share of long balls (${LONG_BALL_LENGTH}+ long) that were completed.`,
      },
      {
        label: "Average X Pass",
        value: successful ? (xTotal / successful).toFixed(1) : "—",
        avgKey: "average_x_pass",
        avgType: "decimal",
        definition: "How far up the pitch completed passes start, on average (0 = own goal line, 114 = the opponents').",
      },
      { label: "Pass Into Pressure %", value: pct1(div(intoPressure, n)), key: "intoPressure", avgKey: "pass_into_pressure_perc", avgType: "percentage", definition: "Share of passes played to a teammate under pressure." },
      { label: "Pass Into Danger %", value: pct1(div(intoDanger, n)), key: "intoDanger", avgKey: "pass_into_danger_perc", avgType: "percentage", definition: "Share of passes played into a dangerous area." },
      {
        label: "Left Foot Pass %",
        value: pct1(div(left, left + right)),
        key: "leftFoot",
        avgKey: "left_foot_perc",
        avgType: "percentage",
        definition: "Share of footed passes made with the left foot (not headers or throw-ins).",
      },
      {
        label: "Final Third Passes",
        value: f3All.toLocaleString(),
        key: "f3",
        definition: "Completed open-play passes that start or end in the final third.",
      },
    ],
  };
}

/** "78.1% vs 75.4%" and "+2.7 pts vs expected". */
export function actualVsExpected(r: PassingReport): { main: string; sub: string } {
  if (r.successPct === null || r.expectedPct === null) return { main: "—", sub: "" };
  const diff = (r.successPct - r.expectedPct) * 100;
  return {
    main: `${(r.successPct * 100).toFixed(1)}% vs ${(r.expectedPct * 100).toFixed(1)}%`,
    sub: `${diff >= 0 ? "+" : "−"}${Math.abs(diff).toFixed(1)} pts vs expected`,
  };
}
